import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Chip,
  DialogContent,
  Drawer,
  Grid,
  IconButton,
  Link,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  BentoCategory,
  OrganizationBulkSendSettings,
  OrganizationNiche,
  OrganizationNicheStatus,
} from "schemas/dashboard";
import { EmailSettingTemplate, EmailSettings } from "schemas/email";
import { CustomEvent } from "schemas/functions";

import StyledVideo from "components/Styled/Video";
import {
  CHANGE_NICHE_PREVIEW,
  HOW_TO_CHANGE_NICHE_VIDEO_LINK,
} from "constants/influencer";
import { Template } from "features/Influencer/ContactList/schema";
import { fetcherAuth } from "utils/api";
import { generateColorFromString } from "utils/color";
import { trackEvent } from "utils/tracking";

import EditFallbackTemplatesDrawer from "../../EditFallbackTemplatesDrawer";
import EditNicheDrawer from "../../EditNicheDrawer";
import NewNicheDrawer from "../../NewNicheDrawer";
import StatusChip from "../../StatusChip";
import { NicheTab } from "../schema";
import DefaultTemplate from "./DefaultTemplate";
import LoadingScreen from "./LoadingScreen";
import styles from "./styles";

interface Props {
  bulkSendSetting: OrganizationBulkSendSettings;
  fallbackTemplates: Template[];
  setFallbackTemplates: Dispatch<SetStateAction<Template[]>>;
  nicheLoading: boolean;
  niches: OrganizationNiche[];
  setNiches: Dispatch<SetStateAction<OrganizationNiche[]>>;
  getNiches: (tab: NicheTab) => Promise<OrganizationNiche[]>;
}

const YourNiches = ({
  fallbackTemplates,
  bulkSendSetting,
  setFallbackTemplates,
  nicheLoading,
  niches,
  setNiches,
  getNiches,
}: Props) => {
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  const { setErrorAlert, setAlert } = useContext(AlertContext);
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);
  const [editNicheIdx, setEditNicheIdx] = useState<number | null>(null);
  const [newNicheOpen, setNewNicheOpen] = useState<boolean>(false);
  const [nicheToCopy, setNicheToCopy] = useState<{
    niche: OrganizationNiche;
    templates: Template[] | null;
  } | null>(null);
  const {
    templates,
    userHasNoTemplates,
    editTemplateDialogId,
    setEditTemplateDialogId,
  } = useContext(OutreachTemplatesContext);

  const [nicheTemplates, setNicheTemplates] = useState<(Template[] | null)[]>(
    [],
  );
  const [categoriesToPickFrom, setCategoriesToPickFrom] = useState<
    BentoCategory[]
  >([]);
  const [allEmailSettings, setAllEmailSettings] = useState<EmailSettings[]>([]);
  const [fetchLoading, setFetchLoading] = useState<boolean>(true);

  const [tab, setTab] = useState<NicheTab>(NicheTab.all);

  const [isFetched, setIsFetched] = useState<boolean>(false);
  const [openEditFallbackDrawer, setOpenEditFallbackDrawer] =
    useState<boolean>(false);
  const fetchEmailSettings = async () => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/email-settings`,
        "GET",
      );
      setAllEmailSettings(res.emailSettings);
      return res.emailSettings;
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const computeNicheTemplates = (
    nichesRes: OrganizationNiche[],
    emailSettingsRes: EmailSettings[],
  ) => {
    const templatesById = new Map<number, Template>();
    for (const template of templates) {
      if (template.id) {
        templatesById.set(template.id, template);
      }
    }

    const emailSettingIdToTemplateIds = new Map<number, number[]>();
    for (const s of emailSettingsRes ?? []) {
      const templateIds = s.emailSettingTemplates?.map(
        (settingTemplate: EmailSettingTemplate) => settingTemplate.templateId,
      );
      if (s.id != null && templateIds != null) {
        emailSettingIdToTemplateIds.set(s.id, templateIds);
      }
    }

    const computedNicheTemplates: (Template[] | null)[] = (nichesRes ?? []).map(
      (niche: OrganizationNiche) => {
        const templateIds = niche.emailSettingId
          ? emailSettingIdToTemplateIds.get(niche.emailSettingId)
          : undefined;
        if (!templateIds) return null;
        return templateIds
          .map((templateId) => templatesById.get(templateId))
          .filter((t): t is Template => t !== undefined);
      },
    );

    setNicheTemplates(computedNicheTemplates);
  };

  const addNewNiche = (niche: OrganizationNiche) => {
    const newNiches = [niche, ...niches];
    const newEmailSetting = niche.emailSetting;

    if (!newEmailSetting) {
      return;
    }

    let newEmailSettings = [];
    if (
      allEmailSettings.some((setting) => setting.id === newEmailSetting?.id)
    ) {
      newEmailSettings = allEmailSettings.map((emailSetting) => {
        if (emailSetting.id === newEmailSetting?.id) {
          return newEmailSetting;
        }
        return emailSetting;
      });
    } else {
      newEmailSettings = [...allEmailSettings, newEmailSetting];
    }
    setNiches(newNiches);
    setAllEmailSettings(newEmailSettings);
    computeNicheTemplates(newNiches, newEmailSettings);
  };

  const editNiche = (niche: OrganizationNiche) => {
    if (
      tab === NicheTab.active &&
      niche.status === OrganizationNicheStatus.canceled
    ) {
      setNiches((prev) => prev.filter((n) => n.id !== niche.id));
    }
    const newNiches = niches.map((n) => {
      if (n.id === niche.id) {
        return niche;
      }
      return n;
    });
    const newEmailSetting = niche.emailSetting;

    if (!newEmailSetting) {
      return;
    }

    let newEmailSettings = [];
    if (
      allEmailSettings.some((setting) => setting.id === newEmailSetting?.id)
    ) {
      newEmailSettings = allEmailSettings.map((emailSetting) => {
        if (emailSetting.id === newEmailSetting?.id) {
          return newEmailSetting;
        }
        return emailSetting;
      });
    } else {
      newEmailSettings = [...allEmailSettings, newEmailSetting];
    }
    setNiches(newNiches);
    setAllEmailSettings(newEmailSettings);
    computeNicheTemplates(newNiches, newEmailSettings);
  };

  const loadData = async () => {
    setFetchLoading(true);
    try {
      const [nichesRes, emailSettingsRes] = await Promise.all([
        getNiches(tab),
        fetchEmailSettings(),
      ]);
      computeNicheTemplates(nichesRes, emailSettingsRes);
    } finally {
      setFetchLoading(false);
    }
  };

  const selectedNiche = editNicheIdx !== null ? niches[editNicheIdx] : null;

  const getCategoriesToPickFrom = async () => {
    setFetchLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/bento-categories/onboarding?for_va=${true}`,
      );
      setCategoriesToPickFrom(res.onboardingBentoCategories);
    } catch (error) {
      setAlert(
        error?.message ||
          "Something went wrong. Please refresh and retry again",
        "error",
      );
    } finally {
      setFetchLoading(false);
    }
  };

  useEffect(() => {
    if ((templates.length > 0 || userHasNoTemplates) && !isFetched) {
      loadData();
      setIsFetched(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates.length, userHasNoTemplates]);

  useEffect(() => {
    if (currentOrg?.id && categoriesToPickFrom?.length === 0)
      getCategoriesToPickFrom();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  if (fetchLoading) {
    return <LoadingScreen />;
  }

  return (
    <>
      {isMobileScreen && (
        <Grid>
          <Typography
            variant="h6"
            sx={{ fontSize: "15px", mt: 1, fontWeight: 600 }}
            gutterBottom
          >
            Your Niches
          </Typography>
        </Grid>
      )}

      <Box sx={{ mb: 4 }}>
        {/* Default Template section */}
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
          <Box
            component="span"
            className="fa-solid fa-circle-exclamation"
            sx={{ mr: 1, color: "primary.main" }}
          />
          Default Templates (Required)
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 1.5 }}
        >
          Use when brand category is unknown
        </Typography>

        <DefaultTemplate
          setOpenEditFallbackDrawer={setOpenEditFallbackDrawer}
          fallbackTemplates={fallbackTemplates}
        />

        {/* Category-Specific Templates section */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          sx={{ mb: 0.5 }}
        >
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Category-Specific Templates
              </Typography>
              {!isMobileScreen && (
                <Link
                  href={HOW_TO_CHANGE_NICHE_VIDEO_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    fontSize: 11,
                    color: "text.secondary",
                    textDecoration: "none",
                    mb: 0.5,
                    "&:hover": { color: "primary.main" },
                  }}
                >
                  <Box
                    component="i"
                    className="fa-regular fa-circle-play"
                    sx={{ fontSize: 13 }}
                  />
                  Watch tutorial
                </Link>
              )}
            </Box>
            <Typography variant="caption" color="text.secondary">
              Assign templates to specific categories for better targeting
            </Typography>
          </Box>
          <LoadingButton
            variant="contained"
            color="primary"
            disableElevation
            onClick={(e: CustomEvent) => {
              e.stopPropagation();
              setNewNicheOpen(true);
              trackEvent("VA Settings Page Add Niche Clicked");
            }}
            sx={{ fontSize: { xs: 12, md: 14 }, textTransform: "none" }}
            size={isMobileScreen ? "small" : "medium"}
            loading={nicheLoading}
          >
            <Box component="span" className="fa-solid fa-plus" sx={{ mr: 1 }} />
            Add a niche
          </LoadingButton>
        </Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ my: 2 }}
        >
          <ToggleButtonGroup
            color="primary"
            value={tab}
            exclusive
            aria-label="Platform"
            sx={styles.toggleGroup}
            onChange={(_, value: NicheTab) => {
              setTab(value);
              getNiches(value);
              trackEvent("VA Settings Page Changed Tab", {
                tab: value,
              });
            }}
          >
            <ToggleButton sx={styles.toggle} value={NicheTab.all}>
              Show All Niches
            </ToggleButton>
            <ToggleButton sx={styles.toggle} value={NicheTab.active}>
              Only Active
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
          <Grid container spacing={1} sx={{ flex: 1, minWidth: 0 }}>
            {nicheLoading && (
              <Grid container spacing={1} xs={12} sx={{ mt: -5 }}>
                <Grid xs={12} sm={6} lg={4} item>
                  <Skeleton height="200px" />
                </Grid>
                <Grid xs={12} sm={6} lg={4} item>
                  <Skeleton height="200px" />
                </Grid>
                <Grid xs={12} sm={6} lg={4} item>
                  <Skeleton height="200px" />
                </Grid>
              </Grid>
            )}
            {!nicheLoading &&
              niches.map((niche, idx) => (
                <Grid key={niche.id} xs={12} sm={6} lg={4} item>
                  <Box sx={styles.card}>
                    <Box
                      display="flex"
                      gap={1}
                      flexWrap="wrap"
                      sx={{ mb: 1, mr: 2 }}
                    >
                      <Chip
                        size="small"
                        sx={{
                          backgroundColor: generateColorFromString(
                            niche.name,
                            "tagColorPalette",
                          ),
                          fontSize: 14,
                          px: 1,
                        }}
                        label={niche.name}
                      />
                      {niche.status && <StatusChip status={niche.status} />}
                    </Box>
                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        Outreach Template
                      </Typography>
                    </Box>
                    <Box>
                      {nicheTemplates[idx]?.[0]?.name ? (
                        <Link
                          onClick={() => {
                            const editTemplateId =
                              nicheTemplates[idx]?.[0]?.id?.toString();
                            if (!!editTemplateId) {
                              setEditTemplateDialogId(editTemplateId);
                            }
                            trackEvent(
                              "VA Settings Page Clicked Niche Template",
                              {
                                "Template ID": editTemplateId,
                              },
                            );
                          }}
                          sx={styles.link}
                        >
                          <Typography>
                            {nicheTemplates[idx]?.[0]?.name}
                          </Typography>
                        </Link>
                      ) : (
                        <Link
                          sx={{
                            color: "error.main",
                            textDecoration: "none",
                            cursor: "pointer",
                          }}
                          onClick={(e: CustomEvent) => {
                            e.stopPropagation();
                            setEditNicheIdx(idx);
                            trackEvent(
                              "VA Settings Page Clicked Edit No Template Assigned",
                            );
                          }}
                        >
                          ⚠️ No template assigned
                        </Link>
                      )}
                    </Box>
                    <Box sx={styles.editButtonWrapper}>
                      <Tooltip
                        title={
                          niche.status === OrganizationNicheStatus.pending
                            ? "This niche cannot be edited while the status is pending. We are currently setting up your niche"
                            : null
                        }
                        enterTouchDelay={0}
                      >
                        <span>
                          <IconButton
                            size="small"
                            disabled={
                              niche.status === OrganizationNicheStatus.pending
                            }
                            sx={styles.editButton}
                            onClick={(e: CustomEvent) => {
                              e.stopPropagation();
                              setEditNicheIdx(idx);
                              trackEvent(
                                "VA Settings Page Clicked Edit Niche",
                                {
                                  "Template Index": idx,
                                },
                              );
                            }}
                          >
                            <Box
                              component="span"
                              className="fa-regular fa-pen"
                            />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip
                        title={
                          niche.status === OrganizationNicheStatus.pending
                            ? "This niche cannot be copied while the status is pending. We are currently setting up your niche"
                            : "Copy Niche"
                        }
                        enterTouchDelay={0}
                      >
                        <IconButton
                          size="small"
                          disabled={
                            niche.status === OrganizationNicheStatus.pending
                          }
                          sx={styles.editButton}
                          onClick={(e: CustomEvent) => {
                            e.stopPropagation();
                            setNicheToCopy({
                              niche,
                              templates: nicheTemplates[idx],
                            });
                            setNewNicheOpen(true);
                            trackEvent("VA Settings Page Clicked Copy Niche", {
                              "Niche ID": niche.id,
                            });
                          }}
                        >
                          <Box
                            component="span"
                            className="fa-regular fa-copy"
                          />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </Grid>
              ))}
          </Grid>

          {!isMobileScreen && (
            <Box sx={{ width: 220, flexShrink: 0 }}>
              <StyledVideo
                image={CHANGE_NICHE_PREVIEW}
                videoPreview={CHANGE_NICHE_PREVIEW}
                link={HOW_TO_CHANGE_NICHE_VIDEO_LINK}
                title="AI Virtual Assistant: How to Add or Change a Niche"
                isSmall
                isCentered
              />
            </Box>
          )}
        </Box>

        <EditNicheDrawer
          editNiche={editNiche}
          removeNiche={(niche: OrganizationNiche) => {
            setNiches((prev) => prev.filter((n) => n.id !== niche.id));
          }}
          handleClose={() => {
            if (editTemplateDialogId === null) {
              setEditNicheIdx(null);
            }
          }}
          templates={nicheTemplates[editNicheIdx || 0] || null}
          selectedNiche={selectedNiche}
          categoriesToPickFrom={categoriesToPickFrom}
          loadData={loadData}
        />
        {bulkSendSetting.emailSettingId && (
          <Drawer
            open={openEditFallbackDrawer}
            anchor="right"
            onClose={() => {
              setOpenEditFallbackDrawer(false);
            }}
            sx={styles.drawer}
          >
            <DialogContent sx={{ mt: 6 }}>
              <EditFallbackTemplatesDrawer
                handleClose={() => {
                  setOpenEditFallbackDrawer(false);
                }}
                templates={fallbackTemplates || null}
                setTemplates={setFallbackTemplates}
                open={openEditFallbackDrawer}
                emailSettingId={bulkSendSetting.emailSettingId}
              />
            </DialogContent>
          </Drawer>
        )}
        <NewNicheDrawer
          addNewNiche={addNewNiche}
          open={newNicheOpen}
          handleClose={() => {
            if (newNicheOpen) {
              setNewNicheOpen(false);
              setNicheToCopy(null);
            }
          }}
          categoriesToPickFrom={categoriesToPickFrom}
          copiedNiche={nicheToCopy?.niche}
          copiedNicheTemplates={nicheToCopy?.templates}
        />
      </Box>
    </>
  );
};

export default YourNiches;
